import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import TrippinPostGrid from '../components/TrippinPostGrid';
import BottomNav from './BottomNav';
import { Menu } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import Settings from './Settings';
import { StarIcon } from '@heroicons/react/24/solid';
import countriesLib from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

countriesLib.registerLocale(enLocale);

function toCountryCode(countryName) {
  if (!countryName) return null;
  const code = countriesLib.getAlpha2Code(countryName, 'en');
  return code ? code.toLowerCase() : null;
}

function Profile() {
  const { userIdPar } = useParams(); 
  const navigate = useNavigate();
  const [userId, setUserId] = useState(userIdPar || null);
  const [countries, setCountries] = useState([]); // country ISO codes (alpha-2, lowercase)
  const [loadingCountries, setLoadingCountries] = useState(true);
  const ratingContainerRef = useRef(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const { userId: loggedInUserId, initialized , setAccessToken } = useAuth();

  const fetchBlockStatus = async () => {
    try {
      const res = await axiosInstance.get(`/block/status`, {
        params: { targetUserId: userId },
      });
      setIsBlocked(res.data.isBlocked);
    } catch (error) {
      console.error('Error fetching block status:', error);
    }
  };

  const [profileData, setProfileData] = useState(null);
  const [employeeBusinesses, setEmployeeBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [canViewLists, setCanViewLists] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [hoverRating, setHoverRating] = useState(null);
  const [myRatingForUser, setMyRatingForUser] = useState(0);
  const [trippinPosts, setTrippinPosts] = useState([]);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryTrippins, setCountryTrippins] = useState([]);
  const [loadingCountryTrippins, setLoadingCountryTrippins] = useState(false);
  const [countryOffset, setCountryOffset] = useState(0);
  const COUNTRY_FETCH_LIMIT = 10;
  const hasMoreTrippins = useRef(true);
  const observer = useRef();
  const [activeTab, setActiveTab] = useState('trippins');
  const tabs = ['trippins', 'map'];

  const handleTabKeyDown = (e) => {
    const currentIndex = tabs.indexOf(activeTab);
    if (e.key === 'ArrowRight') {
      const nextIndex = (currentIndex + 1) % tabs.length;
      setActiveTab(tabs[nextIndex]);
      setTimeout(() => document.getElementById(`tab-${tabs[nextIndex]}`)?.focus(), 0);
    }
    else if (e.key === 'ArrowLeft') {
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      setActiveTab(tabs[prevIndex]);
      setTimeout(() => document.getElementById(`tab-${tabs[prevIndex]}`)?.focus(), 0);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const res = await axiosInstance.get('/profile/countries/get', {
        params: { userId }
      });
      const countryCodes = (res.data.countries || [])
        .map((name) => toCountryCode(name))
        .filter((c) => !!c);
      setCountries(countryCodes);
      console.log('Fetched countries (ISO codes):', countryCodes);
      setLoadingCountries(false);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setLoadingCountries(false);
    }
  };
  
  const fetchTrippinsByCountry = async (country, reset = false) => {
    if (!country) {
      console.error("fetchTrippinsByCountry called with an empty country!");
      return;
    }
  
    if (loadingCountryTrippins || !hasMoreTrippins.current) return;
  
    try {
      setLoadingCountryTrippins(true);
      const viewerId = loggedInUserId;
 
      if (!viewerId) {
        console.error("Viewer ID is missing. Cannot fetch Trippins.");
        return;
      }
  
      console.log(`Fetching Trippins for country: ${country} by user: ${userId}, viewer: ${viewerId}`);
  
      const res = await axiosInstance.get(`/profile/by-country/${userId}`, {
        params: {
          country: country.trim(),
          viewerId,
          limit: COUNTRY_FETCH_LIMIT,
          offset: reset ? 0 : countryOffset,
        },
      });
  
      if (res.data.length < COUNTRY_FETCH_LIMIT) {
        hasMoreTrippins.current = false;
      }
  
      const enrichedTrippins = res.data.map((trippin) => ({
        ...trippin,
        isLiked: !!trippin.isLiked,
        isSaved: !!trippin.isSaved,
        places: (trippin.places || []).map((place) => ({
          ...place,
          isSaved: !!place.is_saved,
          post_place_id: place.post_place_id,
        })),
      }));
      
      if (reset) {
        setCountryTrippins(enrichedTrippins);
        setCountryOffset(COUNTRY_FETCH_LIMIT);
      }
      else {
        setCountryTrippins((prev) => [...prev, ...enrichedTrippins]);
        setCountryOffset((prev) => prev + COUNTRY_FETCH_LIMIT);
      }
  
      setSelectedCountry(country);
  
      if (res.data.length > 0) {
        console.log("Navigating to TrippinPostView...");
        navigate(`/trippin/${userId}/country/${country}`, {
          state: {
            posts: enrichedTrippins.map(trippin => ({
              ...trippin,
              isLiked: !!trippin.isLiked,
              isSaved: !!trippin.isSaved,
              places: trippin.places.map(place => ({
                ...place,
                isSaved: !!place.isSaved,
                post_place_id: place.post_place_id,
              })),
            })),
            selectedPostId: enrichedTrippins[0]?.trippin_post_id,
            isProfile: true,
          },
        });      
      } else {
        alert("No Trippins found for this country.");
      }
  
    } catch (err) {
      console.error("Error fetching Trippins by country:", err);
    } finally {
      setLoadingCountryTrippins(false);
    }
  };
  
  // Intersection Observer to detect when the last post is visible
  const lastTrippinElementRef = useRef(null);
  
  useEffect(() => {
    if (!selectedCountry || countryTrippins.length === 0) return;

    const observerCallback = (entries) => {
      if (entries[0].isIntersecting && hasMoreTrippins.current) {
        fetchTrippinsByCountry(selectedCountry);
      }
    };
  
    observer.current = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    });
  
    if (lastTrippinElementRef.current) {
      observer.current.observe(lastTrippinElementRef.current);
    }
  
    return () => observer.current && observer.current.disconnect();
  }, [selectedCountry, countryTrippins]);
  
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/profile/${userId}`);
      setProfileData(res.data);
      setEmployeeBusinesses(res.data.employeeBusinesses || []);
      console.log(employeeBusinesses);
      console.log('Profile Data:', res.data);
      setIsOwner(res.data.isOwner);
      setCanViewLists(res.data.canViewLists);
      setProfilePicture(res.data.signedUrl);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setLoading(false);
    }
  };

  const handleRatingSubmit = async (newRating) => {
    try {
      const res = await axiosInstance.post(`/profile/${userId}/rate`, { rating: newRating });
      setMyRatingForUser(res.data.rating);
    } catch (err) {
      console.error('Error rating user:', err);
    }
  };

  const loadTrippinPosts = async () => {
    try {
      const res = await axiosInstance.get(`/trippin/user/${userId}?sort=newest`);
      setTrippinPosts(res.data);
    } catch (err) {
      console.error('Error fetching trippin posts:', err);
    }
  };

  const fetchMyRatingForUser = async () => {
    try {
      const raterId = loggedInUserId
      const res = await axiosInstance.get(`/profile/${raterId}/for/${userId}`);
      if (res.data && typeof res.data.rating === 'number') {
        setMyRatingForUser(res.data.rating);
      } else {
        setMyRatingForUser(0);
      }
    } catch (err) {
      console.error('Error fetching rating:', err);
    }
  };

  useEffect(() => {
    if (!loggedInUserId && initialized) {
      console.error('User not logged in. Redirecting to login.');
      navigate('/login', { replace: true });
      return;
    }
  
    if (!userIdPar) {
      setUserId(loggedInUserId);
    } else {
      setUserId(userIdPar);
    }
  }, [userIdPar, navigate, loggedInUserId, initialized]);

  useEffect(() => {
    if (userId) {
      fetchProfile();
      fetchCountries();
      if (userId !== loggedInUserId) {
        fetchMyRatingForUser();
        fetchBlockStatus();
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!loading && profileData) {
      loadTrippinPosts();
    }
  }, [loading, profileData]);

  const openHamburgerMenu = () => setShowHamburgerMenu(true);
  const closeHamburgerMenu = () => setShowHamburgerMenu(false);

  if (loading) {
    return (
      <>
        <div className="flex w-full h-screen items-center justify-center">
          <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
        </div>
        <BottomNav />
      </>
    );
  }

  if (!profileData) return <div>Profile not found</div>;

  const {
    username,
    stalking_count,
    stalkers_count,
    trippin_count,
    hasFlash,
  } = profileData;

  if (loadingCountries) {
    return (
      <div className="flex w-full h-screen items-center justify-center">
        <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
      </div>
    );
  }

  if (showHamburgerMenu) {
    return (
      <>
        <Settings
          isOwner={isOwner}
          employeeBusinesses={employeeBusinesses}
          onClose={closeHamburgerMenu}
          navigate={navigate}
          username={username}
          userId={userId}
          isBlocked={isBlocked}
          setIsBlocked={setIsBlocked}
          setAccessToken={setAccessToken}
        />
        <BottomNav />
      </>
    );
  }

  return (
    <div className="tailwind flex flex-col h-screen max-w-4xl mx-auto px-4 pt-14 pb-20">
      <div className="flex flex-col items-center relative">
        {(isOwner || (!isOwner && !showHamburgerMenu)) && (
          <button
            onClick={openHamburgerMenu}
            aria-label="Menu"
            className="absolute top-0 right-0 p-2 rounded-md focus:outline-none"
          >
            <Menu className="w-6 h-6 text-black dark:text-gray-100" />
          </button>
        )}
        <img
          src={profilePicture || '/assets/images/default_profile.png'}
          alt="Profile"
          className={`w-20 h-20 rounded-full object-cover shadow ${hasFlash ? 'ring-4 ring-mainColor dark:ring-mainLightColor ring-offset-4 ring-offset-white dark:ring-offset-darkBackground' : ''} cursor-pointer`}
          onClick={() => {
            if (hasFlash) navigate(`/user-flashes/${userId}`);
          }}
        />
        <h2 className="mt-4 text-2xl font-bold text-black dark:text-white">{profileData.username}</h2>
        <div className="mt-4 flex items-start justify-center gap-10 text-center">
          {canViewLists && (
            <div
              className="cursor-pointer"
              onClick={() => navigate(`/profile/${userId}/stalking`)}
            >
              <div className="text-lg pb-1 font-semibold leading-tight text-black dark:text-white">{stalking_count}</div>
              <div className="font-poppins text-sm tracking-wide text-black dark:text-gray-100">Stalking</div>
            </div>
          )}
          {canViewLists && (
            <div
              className="cursor-pointer"
              onClick={() => navigate(`/profile/${userId}/stalkers`)}
            >
              <div className="text-lg pb-1 font-semibold leading-tight text-black dark:text-white">{stalkers_count}</div>
              <div className="font-poppins text-sm tracking-wide text-black dark:text-gray-100">Stalkers</div>
            </div>
          )}
          <div>
            <div className="text-lg pb-1 font-semibold leading-tight text-black dark:text-white">{trippin_count}</div>
            <div className="font-poppins text-sm tracking-wide text-black dark:text-gray-100">Trippins</div>
          </div>
        </div>
      </div>

      {!isOwner && (
        <div className="mt-6 flex flex-col items-center">
          {/* <p className="text-sm text-black dark:text-gray-100 mb-2">Your rating for {username}:</p> */}
          <div
            className="flex relative"
            ref={ratingContainerRef}
            onMouseLeave={() => setHoverRating(null)}
            onTouchMove={(e) => {
              const container = ratingContainerRef.current;
              if (!container) return;
              const rect = container.getBoundingClientRect();
              const touchX = e.touches[0].clientX - rect.left;
              const starWidth = rect.width / 5;
              const touchedStar = Math.ceil(touchX / starWidth);
              if (touchedStar < 1) setHoverRating(0);
              else if (touchedStar > 5) setHoverRating(5);
              else setHoverRating(touchedStar);
            }}
            onTouchEnd={() => handleRatingSubmit(hoverRating || 0)}
          >
            {[1, 2, 3, 4, 5].map((starValue) => (
              <label key={starValue} className="cursor-pointer">
                <input
                  type="radio"
                  name="rating"
                  value={starValue}
                  className="hidden"
                  onClick={() => handleRatingSubmit(starValue)}
                />
                <StarIcon
                  className={`w-7 h-7 transition-colors ${
                    starValue <= (hoverRating || myRatingForUser)
                      ? 'text-mainLightColor'
                      : 'text-white'
                  }`}
                  onMouseEnter={() => setHoverRating(starValue)}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <div
          role="tablist"
          aria-label="Profile sections"
          className="flex items-center justify-evenly gap-6 border-b border-gray-200 dark:border-gray-700"
        >
          {tabs.map((tab) => (
            <button
              key={tab}
              id={`tab-${tab}`}
              role="tab"
              aria-selected={activeTab === tab}
              aria-controls={`panel-${tab}`}
              tabIndex={activeTab === tab ? 0 : -1}
              onKeyDown={handleTabKeyDown}
              onClick={() => setActiveTab(tab)}
              className={`-mb-px px-10 pb-3 pt-2 text-base font-medium transition-colors border-b-2 
                ${
                  activeTab === tab
                    ? 'border-black dark:border-white text-black dark:text-white'
                    : 'border-transparent text-gray-600 dark:text-gray-300'
                }`}
            >
              {tab === 'trippins' ? 'Trippins' : 'Map'}
            </button>
          ))}
        </div>
      </div>

      <div
        role="tabpanel"
        id="panel-trippins"
        aria-labelledby="tab-trippins"
        hidden={activeTab !== 'trippins'}
      >
        <div className="mt-8">
          <TrippinPostGrid
            navigate={navigate}
            posts={trippinPosts}
            profilePicture={profileData.signedUrl}
            username={profileData.username}
            isOwner={isOwner}
            isProfile={true}
          />
        </div>
      </div>
      <BottomNav />
    </div>
  );
}

export default Profile;